/**
 * 세션 이벤트 반영 — 제어 평면 통지의 적용 규칙.
 *
 * 통지는 **판정된 사실**을 실어 온다(실행 상태 권위 설계 P4): 종결은
 * TaskEnded.final_state(어댑터 판정, 백스톱 회수는 SM 관측 사실), 비종결
 * 전이는 TaskStateChanged(SM 팬아웃 성공). 과거의 cause→completed 추측
 * 매핑은 삭제됐다 — 그 추측이 stop 응답과 경합해 정지를 COMPLETED 로
 * 표시하던 것이 스테이지 이슈 #1 이다.
 *
 * 판정이 없는 통지(UNSPECIFIED, 구버전 SM 과도기)는 아무것도 바꾸지
 * 않는다 — 모르는 것을 아는 척하지 않는다(P5).
 *
 * 종결 통지는 **왜 끝났는지**도 실어 온다(TaskEnded.detail — 어댑터가 보고한 원문, 보고 없이 끝난 Job 이면
 * 잡 래퍼가 종료 메시지에 남긴 판정의 사유나 SM 이 모은 Job 증거). 그 참가자의
 * `error` 에 그대로 남기고, 화면은 표시 중인 실행(shownRun)의 `endReason` 으로 읽는다.
 *
 * 이벤트의 **수신**(구독·재개 커서)은 탭 통합 스트림(sessionStreamMux)의
 * kind=SESSION_EVENT 라우팅 소관이다 — 이 모듈은 반영 규칙만 남는다.
 */

import { useSimulationStore } from '@/app/stores/simulationStore';
import {
  DeleteTaskRequest_FinalState,
  type SessionEvent,
  type TaskEnded,
  type TaskStateChanged,
} from '@/stubs/sm/simulation_manager_pb';
import { isLiveSimStatus, TERMINAL_SIM_STATUSES, type SimStatus } from '@/types/simulation';
import { smSimStatusName, smSimStatusToSimStatus } from '@/app/services/sm/taskStatus';

const FINAL_STATE_TO_STATUS: Partial<Record<DeleteTaskRequest_FinalState, SimStatus>> = {
  [DeleteTaskRequest_FinalState.COMPLETED]: 'completed',
  [DeleteTaskRequest_FinalState.FAILED]: 'failed',
  [DeleteTaskRequest_FinalState.STOPPED]: 'stopped',
  // 보고도 래퍼의 판정도 없이 Job 이 끝났거나 사라져 SM 이 판정 없이 정리(«죽은 Job» 회수) — 결과는 모른다
  [DeleteTaskRequest_FinalState.TERMINATED]: 'terminated',
};

/** 종결 상태 집합 — 세션 종결 판정의 정본. 소비자(분석 자동 적재 등)는 이것을 가져다 쓴다 */
export const TERMINAL: ReadonlySet<SimStatus> = new Set(TERMINAL_SIM_STATUSES); // 선언은 types/simulation 한 곳

/** 실행 판정의 우선순위 — 참가자 판정 중 가장 앞선 것이 실행의 판정. SM 저널 `SIM_FINAL_STATE_PRECEDENCE`
 *  (failed > terminated > stopped > unspecified > completed)와 같은 표(웹은 unspecified 를 반영하지 않는다). */
export const VERDICT_PRECEDENCE: readonly SimStatus[] = ['failed', 'terminated', 'stopped', 'completed'];

/**
 * 단일 이벤트 반영.
 * @returns 상태를 실제로 바꿨는지 (테스트·로깅용)
 */
export function applySessionEvent(event: SessionEvent): boolean {
  switch (event.event.case) {
    case 'taskEnded':
      return applyTaskEnded(event.event.value, event.seq);
    case 'taskStateChanged':
      return applyTaskStateChanged(event.event.value);
    default:
      return false;
  }
}

/**
 * 받을 실행 상태가 아직 없는 종결 통지 — 태스크 id → (통지, 세션 seq).
 *
 * 세션 스트림은 로그인 순간부터 흐르지만 실행 상태는 늦게 생긴다: 시작은 SM Build(참가자 시작 수락까지)의 응답 뒤,
 * 이어보기는 [이어보기] 뒤. 보내기만 하는 참가자는 그 사이에 끝나고(fmu-fmu-fmu 예제에서 관측), 이어보기는 로그인 순간 재생된
 * 통지를 조립 전에 받는다. 커서는 이미 지났으므로 버리면 다시 오지 않는다.
 *
 * 종결은 단조다(한 번 끝난 참가자는 계속 끝났다) — 늦게 적용해도 옳으므로 붙잡았다가, 실행 상태가 생긴 자리
 * (`applyPendingTaskEnded` — 세션 파이프라인이 텔레메트리 게이트를 방류하는 곳)에서 적용한다. 비종결 전이
 * (TaskStateChanged)는 단조가 아니라 붙잡지 않는다 — 늦게 적용하면 최신 상태를 옛것으로 덮는다.
 */
const pendingEnded = new Map<string, { ended: TaskEnded; seq: bigint }>();

/** 보류 상한 — SM 이 세션당 보존·재생하는 통지 수(session_registry EVENT_RETAIN)와 같은 규모. 넘치면 오래된 것부터 버린다 */
const PENDING_ENDED_LIMIT = 64;

function holdTaskEnded(ended: TaskEnded, seq: bigint): void {
  // 같은 태스크의 통지가 또 오면 나중 것으로 바꾼다 — 실행 상태가 있을 때도 applyTaskEnded 는 다른 판정이면
  // 덮어써서 나중 것으로 끝나므로, 보류분도 같은 결과를 낸다. delete 뒤 set 은 삽입 순서(상한의 «오래된 것»)도 갱신한다
  pendingEnded.delete(ended.taskId);
  pendingEnded.set(ended.taskId, { ended, seq });
  while (pendingEnded.size > PENDING_ENDED_LIMIT) {
    const oldest = pendingEnded.keys().next().value;
    if (oldest === undefined) break;
    pendingEnded.delete(oldest);
  }
  // 로그인 순간의 재생에는 이미 끝난 옛 실행의 통지도 섞여 온다 — 정상 경로라 debug. 적용은 warn 으로 남는다
  console.debug(
    `[sessionEvents] 종료 통지 보류 — 받을 실행 상태 없음: task=${ended.taskId} (simRemoved=${ended.simRemoved})`,
  );
}

/**
 * 보류한 종결 통지 중 지금 실행 상태의 참가자 것을 세션 seq 순으로 적용하고 비운다.
 * 실행 상태가 생긴 자리에서 부른다 — 시작과 이어보기가 함께 지나는 세션 파이프라인의 방류 지점.
 */
export function applyPendingTaskEnded(): void {
  const session = useSimulationStore.getState().simSession;
  if (!session) return;
  const taskIds = new Set(Object.values(session.models).map((m) => m.taskId));
  const due = [...pendingEnded.values()]
    .filter(({ ended }) => taskIds.has(ended.taskId))
    .sort((a, b) => (a.seq < b.seq ? -1 : a.seq > b.seq ? 1 : 0));
  for (const { ended, seq } of due) {
    pendingEnded.delete(ended.taskId);
    console.warn(`[sessionEvents] 보류한 종료 통지 적용: task=${ended.taskId}`);
    applyTaskEnded(ended, seq);
  }
}

/** 세션이 끝나면(로그아웃·세션 교체) 보류분도 끝난다 — 다른 세션의 통지는 이 세션의 실행에 닿지 않는다 */
export function clearPendingTaskEnded(): void {
  pendingEnded.clear();
}

function applyTaskEnded(ended: TaskEnded, seq: bigint): boolean {
  const status = FINAL_STATE_TO_STATUS[ended.finalState];
  if (status === undefined) {
    // 구버전 SM(판정 미탑재) 또는 판정 불가 — 추측으로 메우지 않는다.
    console.warn(
      `[sessionEvents] 판정 없는 종료 통지(final_state=${ended.finalState}) — 전이 생략:`,
      `task=${ended.taskId}`,
    );
    return false;
  }

  const store = useSimulationStore.getState();
  const session = store.simSession;
  const model = session
    ? Object.values(session.models).find((m) => m.taskId === ended.taskId)
    : undefined;
  if (!model) {
    holdTaskEnded(ended, seq);
    return false;
  }
  // 사유는 판정과 따로 남긴다 — 스트림 오류가 먼저 실패로 바꿔 놓았으면 판정은 이미 같지만 사유는 아직 없다.
  // 사유 없는 통지는 이미 있는 사유를 지우지 않는다(값 부재는 «모름» 이지 «없음» 이 아니다)
  const reason = ended.detail && ended.detail !== model.error ? { error: ended.detail } : {};
  if (model.status === status) {
    if ('error' in reason) store.updateModelByTaskId(ended.taskId, reason);
    return false;
  }

  console.warn(
    `[sessionEvents] 종료 통지 반영: task=${ended.taskId} → ${status} (simRemoved=${ended.simRemoved})`,
  );
  store.updateModelByTaskId(ended.taskId, {
    status,
    endTime: model.endTime ?? Date.now(),
    ...reason,
  });

  if (ended.simRemoved) {
    settleSessionFromModels();
  }
  return true;
}

/** 모델별 판정(사실)의 집계로 세션을 종결짓는다 — 전부 종결일 때만. */
function settleSessionFromModels(): void {
  const store = useSimulationStore.getState();
  const models = store.simSession ? Object.values(store.simSession.models) : [];
  if (models.length === 0 || !models.every((m) => TERMINAL.has(m.status))) return;
  const sessionStatus = VERDICT_PRECEDENCE.find((s) => models.some((m) => m.status === s)) ?? 'completed';
  store.setSessionStatus(sessionStatus);
}

/**
 * 비종결 전이(pause/resume) — 다른 세션의 조작을 공동 관찰자 화면에 반영.
 * 조작한 세션 자신은 RPC 응답으로 이미 확정했으므로(P1) 같은 값의 재적용은
 * 무해하다. 종결된 모델은 건드리지 않는다.
 */
function applyTaskStateChanged(changed: TaskStateChanged): boolean {
  // TaskStateChanged.status 는 SM 의 실행 상태 enum 이다(`taskStatus` 의 표가 읽는다). 이 통지가 나르는 것은 진행 중 전이
  // (일시정지·재개)뿐이다 — 종결은 TaskEnded 가, 짓는 중은 Build 의 답이 말한다. 그 밖의 상태는 생략한다
  const status = smSimStatusToSimStatus(changed.status);
  if (!isLiveSimStatus(status)) {
    console.warn(
      `[sessionEvents] 진행 중 전이가 아닌 상태(${smSimStatusName(changed.status)}) — 생략: sim=${changed.simId}`,
    );
    return false;
  }
  const store = useSimulationStore.getState();
  const session = store.simSession;
  if (!session || session.simId !== changed.simId) return false;

  let changedAny = false;
  for (const model of Object.values(session.models)) {
    // 상태 모름인 참가자는 세션 단위 전이가 덮지 않는다 — 그 참가자에 대해 아무것도 말해 주지 않는다.
    // 참가자별 실제 상태는 다음 실행 제어 응답이 태스크마다 다시 싣는다.
    if (TERMINAL.has(model.status) || model.status === 'unknown' || model.status === status) continue;
    store.updateModelByTaskId(model.taskId, { status });
    changedAny = true;
  }
  if (session.status !== status && !TERMINAL.has(session.status)) {
    store.setSessionStatus(status);
    changedAny = true;
  }
  if (changedAny) {
    console.warn(`[sessionEvents] 전이 통지 반영: sim=${changed.simId} → ${status}`);
  }
  return changedAny;
}
